<template>
  <div class="forest-plots">
    <div class="instruction">
      Select Transition to plot Hazard Ratios (or click number in the graph):
      <div class="ui inline dropdown">
        <sui-dropdown
          class="event-dropdown"
          fluid
          :options="stateOptions"
          search
          selection
          text="Select Transition"
          v-model="transition"
        />
      </div>
      <transition-plot :active="transition" />
    </div>

    <h4 class="titles" v-html="tableTitle"></h4>

    <sui-table size="small" compact="very" celled padded>
      <sui-table-header>
        <sui-table-row text-align="center">
          <sui-table-header-cell single-line>Component</sui-table-header-cell>
          <sui-table-header-cell>Hazard Ratio [95% CI]</sui-table-header-cell>
          <sui-table-header-cell class="hazard-ratio-header-plot">
            <svg :width="width + 8" height="50" class="plot-cell">
              <line :x1="getPosx(2)" y1="30" :x2="getPosx(2)" y2="50"></line>
              <line
                :x1="getPosx(1)"
                y1="30"
                :x2="getPosx(1)"
                y2="50"
                class="bolder"
              ></line>
              <line
                :x1="getPosx(0.5)"
                y1="30"
                :x2="getPosx(0.5)"
                y2="50"
              ></line>
              <line
                :x1="getPosx(0.2)"
                y1="30"
                :x2="getPosx(0.2)"
                y2="50"
              ></line>

              <text :x="getPosx(0.2)" y="22">0.2</text>
              <text :x="getPosx(0.5)" y="22">0.5</text>
              <text :x="getPosx(1)" y="22">1</text>
              <text :x="getPosx(2)" y="22">2</text>
            </svg>
          </sui-table-header-cell>
          <sui-table-header-cell>p-value</sui-table-header-cell>
        </sui-table-row>
      </sui-table-header>

      <sui-table-body>
        <sui-table-row v-for="[field, fieldData] in tableData" :key="field">
          <sui-table-cell>
            <h5 is="sui-header" text-align="left" class="hazard-components">
              {{ componentLabels[field] }}
            </h5>
          </sui-table-cell>
          <sui-table-cell text-align="center">
            <div v-if="fieldData.hr === 'ref'">
              reference
            </div>
            <div v-else-if="!fieldData.hr">
              N/A
            </div>
            <div v-else>
              {{ fieldData.hr | round(3) }}, [{{ fieldData.lower | round(3) }} -
              {{ fieldData.upper | round(3) }}]
            </div>
          </sui-table-cell>
          <sui-table-cell text-align="right">
            <svg :width="width + 40" height="30" class="plot-cell">
              <g>
                <line :x1="getPosx(2)" y1="0" :x2="getPosx(2)" y2="30"></line>
                <line
                  :x1="getPosx(1)"
                  y1="0"
                  :x2="getPosx(1)"
                  y2="30"
                  class="bolder"
                ></line>
                <line
                  :x1="getPosx(0.5)"
                  y1="0"
                  :x2="getPosx(0.5)"
                  y2="30"
                ></line>
                <line
                  :x1="getPosx(0.2)"
                  y1="0"
                  :x2="getPosx(0.2)"
                  y2="30"
                ></line>
              </g>
              <g v-if="!fieldData.hr || fieldData.hr === 'ref'">
                <rect
                  width="8"
                  height="8"
                  :transform="`translate(${getPosx(1) - 4}, 11)`"
                  class="square"
                ></rect>
              </g>
              <g v-else>
                <rect
                  width="8"
                  height="8"
                  :transform="`translate(${getPosx(fieldData.hr) - 4}, 11)`"
                  class="square"
                ></rect>
                <rect
                  width="1"
                  height="8"
                  :transform="`translate(${getPosx(fieldData.lower) - 1}, 11)`"
                  class="square"
                ></rect>
                <rect
                  width="1"
                  height="8"
                  :transform="`translate(${getPosx(fieldData.upper) - 1}, 11)`"
                  class="square"
                ></rect>
                <rect
                  :width="
                    `${getPosx(fieldData.upper) - getPosx(fieldData.lower)}`
                  "
                  height="1"
                  :transform="`translate(${getPosx(fieldData.lower)}, 14.5)`"
                  class="square"
                ></rect>
              </g>
            </svg>
          </sui-table-cell>
          <sui-table-cell style="padding-left: 12px">
            <div>{{ fieldData.pval | parsePValue }}</div>
          </sui-table-cell>
        </sui-table-row>
      </sui-table-body>
    </sui-table>
  </div>
</template>

<script>
import TransitionPlot from "@/components/TransitionPlot";
import {
  stateTransitions,
  forestData,
  componentLabels
} from "@/utils/transitions";

export default {
  name: "ForestPlots",
  components: {
    TransitionPlot
  },
  data() {
    const stateOptions = Object.values(stateTransitions).map(
      ({ title }, index) => {
        const text = title.replace(" &#8594; ", " to ");
        return {
          text,
          value: index + 1
        };
      }
    );
    return {
      transition: 1,
      forestData,
      componentLabels,
      stateTransitions,
      stateOptions,
      width: 200
    };
  },
  mounted() {
    document.querySelectorAll(".transition-plot svg .number").forEach(el => {
      el.addEventListener("click", this.switchTransition);
    });
  },
  computed: {
    tableData() {
      return Object.entries(this.forestData[this.transition]);
    },
    tableTitle() {
      const { title, percentage, total } = Object.values(this.stateTransitions)[
        this.transition - 1
      ];
      return `${title}&emsp;(n=${total})&emsp;${percentage}`;
    },
    min() {
      return Math.min(
        ...this.tableData
          .map(i => i[1].lower)
          .filter(i => i && i !== "ref")
          .map(i => Number(i))
      );
    },
    max() {
      return Math.max(
        ...this.tableData
          .map(i => i[1].upper)
          .filter(i => i && i !== "ref")
          .map(i => Number(i))
      );
    }
  },
  filters: {
    parseField(value) {
      return this.componentLabels.get(value);
    },
    parsePValue(pValue) {
      if (pValue === "ref") {
        return "reference";
      } else if (!pValue) {
        return "N/A";
      } else {
        const [value, ...ePower] = pValue.split("e");
        return `${+Number(value).toFixed(3)} ${
          ePower.length ? "e" + ePower[0] : ""
        }`;
      }
    }
  },
  methods: {
    getPosx(value) {
      return (
        (this.width * (Math.log(value) - Math.log(this.min))) /
          (Math.log(this.max) - Math.log(this.min)) +
        this.max
      );
    },
    switchTransition(el) {
      if (el.target.classList.contains("state-1")) {
        this.transition = 1;
      } else if (el.target.classList.contains("state-2")) {
        this.transition = 2;
      } else if (el.target.classList.contains("state-3")) {
        this.transition = 3;
      } else if (el.target.classList.contains("state-4")) {
        this.transition = 4;
      } else if (el.target.classList.contains("state-5")) {
        this.transition = 5;
      }
    }
  }
};
</script>

<style lang="scss">
.forest-plots {
  .instruction {
    color: #00113d;
    text-align: center;
    line-height: 3rem;
    .ui.inline.dropdown {
      margin-left: 10px;
      .dropdown.icon {
        margin: -0.8em;
      }
    }
    .event-dropdown.ui.fluid.selection.search.dropdown {
      min-width: 370px;
    }
  }
  h4.titles {
    color: #00113d;
    text-align: center;
  }
  .ui.table th.hazard-ratio-header-plot {
    padding: 0em;
  }
  .ui.table.celled.padded td {
    padding: 0em;
    line-height: 1em;
  }
  .ui[class*="very compact"].table th {
    padding-top: 0rem;
    padding-bottom: 0rem;
  }
  h5.ui.header.hazard-components {
    padding-left: 12px;
    font-size: 11px;
  }
  svg.plot-cell {
    overflow: auto;
    align-items: center;
    display: flex;
    // margin: auto;

    .square {
      fill: black;
    }

    line {
      stroke: #ccc;
      stroke-width: 1;
      stroke-dasharray: 5 5;
      &.bolder {
        stroke: #888;
      }
    }
    text {
      fill: #555;
      font-size: 10px;
      text-anchor: middle;
    }
  }
}
</style>
